import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Landingheader from './Landingheader';
import Footer from '../Footer';

export default function RequestProgress() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        // Change to your absolute URL if backend is on a different port (e.g., 'http://localhost:5000/getApprovedProgress')
        const response = await axios.get('https://land-survey-main-server.onrender.com/getApprovedProgress');
        const data = response.data;

        if (data.status === true) {
          setActivities(data.activities);
        } else {
          setError(data.message || "No approved updates found.");
        }
      } catch (err) {
        console.error("Axios network error:", err);
        setError("Failed to connect to the server.");
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, []);

  const totalSteps = activities.length;
  const latestActivityIndex = totalSteps - 1;
  const latestActivity = activities[latestActivityIndex];

  // Use the percentage column from your latest database entry dynamically!
  const progressPercentage = latestActivity ? latestActivity.progress_percentage || 0 : 0; 

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="w-10 h-10 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Landingheader />
        <div className="max-w-2xl p-6 mx-auto my-6 text-center bg-white border border-gray-100 shadow-md rounded-2xl">
          <p className="font-medium text-gray-600">{error}</p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Landingheader />
      <div className="max-w-2xl p-6 mx-auto my-6 font-sans bg-white border border-gray-100 shadow-md rounded-2xl">
        
        {/* Header Section */}
        <div className="flex flex-col pb-6 border-b border-gray-100 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="px-2 py-1 text-xs font-semibold tracking-wider text-indigo-600 uppercase rounded-md bg-indigo-50">
              Project ID: {latestActivity?.project_id || "N/A"}
            </span>
            <h2 className="mt-2 text-xl font-bold text-gray-800">
              {/* Uses your exact database column for the main title */}
              {latestActivity?.progress_title || "Project Activities"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Latest update recorded on {latestActivity?.created_at ? new Date(latestActivity.created_at).toLocaleDateString() : ""}
            </p>
          </div>
          
          <div className="flex items-center mt-4 sm:mt-0">
            <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-800 bg-blue-100 rounded-full">
              <span className="w-2 h-2 mr-2 bg-blue-500 rounded-full animate-pulse"></span>
              In Progress
            </span>
          </div>
        </div>

        {/* Dynamic Visual Progress Bar */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold tracking-wider text-gray-500 uppercase">Overall Status</span>
            <span className="text-sm font-bold text-indigo-600">{progressPercentage}% Completed</span>
          </div>
          <div className="w-full h-3 overflow-hidden bg-gray-100 border rounded-full border-gray-200/50">
            <div 
              className="h-full transition-all duration-500 ease-out bg-indigo-600 rounded-full"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {/* Latest Summary Note Banner */}
        {latestActivity?.progress_summary && (
          <div className="flex items-start p-4 my-6 space-x-3 border border-gray-100 bg-gray-50 rounded-xl">
            <svg className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-gray-700">Latest Field Update:</h4>
              <p className="mt-1 text-sm italic text-gray-600">"{latestActivity.progress_summary}"</p>
            </div>
          </div>
        )}

        {/* Progress Timeline Tracker */}
        <div className="relative mt-6 pl-6 space-y-6 before:absolute before:bottom-2 before:top-2 before:left-2 before:w-0.5 before:bg-gray-200">
          {activities.map((step, index) => {
            const isLatest = index === latestActivityIndex;
            
            return (
              <div key={step.id || index} className="relative flex items-start space-x-4">
                {/* Timeline Icon indicator */}
                <div className="absolute flex items-center justify-center mt-1 -left-6">
                  {isLatest ? (
                    <span className="relative flex h-4 w-4 mx-0.5 bg-white rounded-full">
                      <span className="absolute inline-flex w-full h-full bg-indigo-400 rounded-full opacity-75 animate-ping"></span>
                      <span className="relative inline-flex w-4 h-4 bg-indigo-600 rounded-full"></span>
                    </span>
                  ) : (
                    <svg className="w-5 h-5 text-green-500 bg-white rounded-full" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>

                {/* Step Content */}
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-semibold ${isLatest ? 'text-indigo-600 font-bold' : 'text-gray-800'}`}>
                      {/* Uses your database row field progress_title */}
                      {step.progress_title} 
                    </h3>
                    {step.created_at && (
                      <span className="text-xs text-gray-400">
                        {new Date(step.created_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-0.5 text-gray-500">
                    {/* Uses your database row field progress_summary */}
                    {step.progress_summary}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

      </div>
      <Footer />
    </>
  );
}